import "./CreateNotification.scss";
import AddPhoto from "./../../../assets/add-photo.png";
import { useState, useEffect, useCallback } from "react";
import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import BlueButton from "../../../components/BlueButton/BlueButton";
import api from "../../../utils/api";
import {
  NotificationMessagesTechniciansColumns,
  NotificationMessagesUsersColumns,
} from "../../../SourceData";

const CreateNotification = () => {
  // Form state
  const [formData, setFormData] = useState({
    title: { ar: "", en: "" },
    text: { ar: "", en: "" },
    receiverType: "all", // Default to "all"
    notificationImage: null,
    receiverIds: [],
    receiverIdsType: "", // This will be set when needed
  });

  // UI states
  const [image, setImage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Data states
  const [users, setUsers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedReceivers, setSelectedReceivers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Debounce search term changes
  useEffect(() => {
    const handler = setTimeout(() => {
      // Only update debounced search term if length is 0 or at least 3 characters
      if (searchTerm.length === 0 || searchTerm.length >= 3) {
        setDebouncedSearchTerm(searchTerm);
      }
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Handle form field changes
  const handleInputChange = (field, subfield, value) => {
    if (subfield) {
      setFormData({
        ...formData,
        [field]: {
          ...formData[field],
          [subfield]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }
  };

  // Handle file upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setFormData({
        ...formData,
        notificationImage: file,
      });
    }
  };

  // Handle receiver type change
  const handleReceiverTypeChange = (event) => {
    const value = event.target.value;

    // Reset search term and selected receivers when changing type
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setSelectedReceivers([]);

    // Map the UI selection values to API values
    let apiReceiverType = value;
    let receiverIdsType = "";

    // Handle mapping to the desired API format
    if (value === "specific-users") {
      apiReceiverType = "custom";
      receiverIdsType = "user";
      // Reset page when changing type
      setPage(1);
    } else if (value === "specific-technicians") {
      apiReceiverType = "custom";
      receiverIdsType = "technician";
      // Reset page when changing type
      setPage(1);
    }

    setFormData({
      ...formData,
      receiverType: apiReceiverType,
      receiverIds: [], // Reset receiver IDs
      receiverIdsType: receiverIdsType,
    });
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Fetch users - memoized to prevent recreation on each render
  const fetchUsers = useCallback(async () => {
    if (formData.receiverIdsType !== "user") return;

    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Only include search term if we have a valid one
      if (debouncedSearchTerm) {
        params.q = debouncedSearchTerm;
      }

      const response = await api.get("/admin/users", { params });

      if (response.data.status === "success") {
        setUsers(response.data.data);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("فشل في جلب المستخدمين. يرجى المحاولة مرة أخرى.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearchTerm, formData.receiverIdsType]);

  // Fetch technicians - memoized to prevent recreation on each render
  const fetchTechnicians = useCallback(async () => {
    if (formData.receiverIdsType !== "technician") return;

    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Only include search term if we have a valid one
      if (debouncedSearchTerm) {
        params.q = debouncedSearchTerm;
      }

      const response = await api.get("/technicians", { params });

      if (response.data.status === "success") {
        setTechnicians(response.data.data);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching technicians:", error);
      toast.error("فشل في جلب الفنيين. يرجى المحاولة مرة أخرى.");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearchTerm, formData.receiverIdsType]);

  // Effect for fetching data when receiverType or page/pageSize changes
  useEffect(() => {
    if (formData.receiverType === "custom") {
      if (formData.receiverIdsType === "user") {
        fetchUsers();
      } else if (formData.receiverIdsType === "technician") {
        fetchTechnicians();
      }
    }
  }, [
    formData.receiverType,
    formData.receiverIdsType,
    fetchUsers,
    fetchTechnicians,
  ]);

  // Effect for responding to debounced search term changes
  useEffect(() => {
    if (formData.receiverType === "custom") {
      // Reset to first page when search term changes
      setPage(1);

      if (formData.receiverIdsType === "user") {
        fetchUsers();
      } else if (formData.receiverIdsType === "technician") {
        fetchTechnicians();
      }
    }
  }, [
    debouncedSearchTerm,
    formData.receiverType,
    formData.receiverIdsType,
    fetchUsers,
    fetchTechnicians,
  ]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append("title[ar]", formData.title.ar);
      formDataToSend.append("title[en]", formData.title.en);
      formDataToSend.append("text[ar]", formData.text.ar);
      formDataToSend.append("text[en]", formData.text.en);
      formDataToSend.append("receiverType", formData.receiverType);

      if (formData.notificationImage) {
        formDataToSend.append("notificationImage", formData.notificationImage);
      }

      // Add receiver IDs if custom receivers are selected
      if (
        formData.receiverType === "custom" &&
        formData.receiverIds.length > 0
      ) {
        // Convert the array to JSON string and append it as a single value
        formDataToSend.append(
          "receiverIds",
          JSON.stringify(formData.receiverIds)
        );
        formDataToSend.append("receiverIdsType", formData.receiverIdsType);
      }

      const response = await api.post("/notifications", formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status === "success") {
        toast.success("تم إنشاء الإشعار بنجاح");
        // Reset form
        setFormData({
          title: { ar: "", en: "" },
          text: { ar: "", en: "" },
          receiverType: "all",
          notificationImage: null,
          receiverIds: [],
          receiverIdsType: "",
        });
        setImage(null);
        setSelectedReceivers([]);
        setSearchTerm("");
        setDebouncedSearchTerm("");
      } else {
        throw new Error(response.data.message || "فشل في إنشاء الإشعار");
      }
    } catch (error) {
      console.error("Error creating notification:", error);
      toast.error(
        error.response?.data?.message ||
          "فشل في إنشاء الإشعار. يرجى المحاولة مرة أخرى."
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle selection of receivers in the DataGrid
  const handleSelectionModelChange = (selectionModel) => {
    const dataArray = formData.receiverIdsType === "user" ? users : technicians;
    const selectedIds = selectionModel.map((id) => {
      const item = dataArray.find((item) => item.id === id || item._id === id);
      return item?._id || id;
    });

    setFormData({
      ...formData,
      receiverIds: selectedIds,
    });
    setSelectedReceivers(selectionModel);
  };

  // Get the appropriate columns and data based on selection
  const getGridColumnsAndData = () => {
    if (formData.receiverIdsType === "user") {
      return {
        columns: NotificationMessagesUsersColumns,
        data: users.map((user, index) => ({
          ...user,
          id: user._id || index,
          index: (page - 1) * pageSize + index + 1,
        })),
      };
    } else {
      return {
        columns: NotificationMessagesTechniciansColumns,
        data: technicians.map((tech, index) => ({
          ...tech,
          id: tech._id || index,
          index: (page - 1) * pageSize + index + 1,
        })),
      };
    }
  };

  const { columns, data } = formData.receiverIdsType
    ? getGridColumnsAndData()
    : { columns: [], data: [] };

  return (
    <div className="create-notification">
      <div className="special-header">
        <p className="title">اضافة اشعار جديد</p>
        <div className="form-img">
          <label htmlFor="image" className="img-control">
            <img
              src={
                image instanceof File ? URL.createObjectURL(image) : AddPhoto
              }
              alt=""
            />
            <span>اضافة صورة الشعار</span>
          </label>
          <input type="file" id="image" onChange={handleImageChange} />
        </div>
      </div>

      <div className="notification-container">
        <form onSubmit={handleSubmit}>
          <div className="control-group">
            <FormControl fullWidth variant="outlined">
              <InputLabel
                htmlFor="title-ar"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                عنوان النص بالعربي
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="title-ar"
                type="text"
                notched
                value={formData.title.ar}
                onChange={(e) =>
                  handleInputChange("title", "ar", e.target.value)
                }
              />
            </FormControl>

            <FormControl fullWidth variant="outlined">
              <InputLabel
                htmlFor="title-en"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                عنوان النص بالانجليزي
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="title-en"
                type="text"
                notched
                value={formData.title.en}
                onChange={(e) =>
                  handleInputChange("title", "en", e.target.value)
                }
              />
            </FormControl>
          </div>

          <div className="control-group">
            <FormControl fullWidth variant="outlined">
              <InputLabel
                htmlFor="text-ar"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                النص بالعربي
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="text-ar"
                type="text"
                notched
                multiline
                minRows={5}
                value={formData.text.ar}
                onChange={(e) =>
                  handleInputChange("text", "ar", e.target.value)
                }
              />
            </FormControl>

            <FormControl fullWidth variant="outlined">
              <InputLabel
                htmlFor="text-en"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.8)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                النص بالانجليزي
              </InputLabel>
              <OutlinedInput
                sx={{ borderRadius: "8px" }}
                required
                id="text-en"
                type="text"
                notched
                multiline
                minRows={5}
                value={formData.text.en}
                onChange={(e) =>
                  handleInputChange("text", "en", e.target.value)
                }
              />
            </FormControl>
          </div>

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel
              htmlFor="receiver-type"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
                textAlign: "right",
              }}
            >
              حدد المستلم
            </InputLabel>
            <Select
              labelId="receiver-type-label"
              id="receiver-type"
              displayEmpty={true}
              sx={{ borderRadius: "8px" }}
              value={
                formData.receiverType === "custom"
                  ? formData.receiverIdsType === "user"
                    ? "specific-users"
                    : "specific-technicians"
                  : formData.receiverType
              }
              onChange={handleReceiverTypeChange}
            >
              <MenuItem value="all">الجميع</MenuItem>
              <MenuItem value="user">المستخدمين</MenuItem>
              <MenuItem value="technician">العمال</MenuItem>
              <MenuItem value="specific-users">تحديد مستخدمين</MenuItem>
              <MenuItem value="specific-technicians">تحديد عمال</MenuItem>
            </Select>
          </FormControl>

          <BlueButton
            text={
              submitLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "ارسال"
              )
            }
            size="medium"
            variant="fill"
            type="submit"
            disabled={submitLoading}
          />
        </form>

        {formData.receiverType === "custom" && (
          <div className="left">
            <TextField
              id="outlined-search"
              type="search"
              value={searchTerm}
              placeholder={
                searchTerm.length < 3 && searchTerm.length > 0
                  ? "يرجى إدخال 3 أحرف على الأقل للبحث..."
                  : "ابحث..."
              }
              onChange={handleSearch}
              sx={{ width: "100%", mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <DataGrid
              sx={{ backgroundColor: "white", width: "100%" }}
              columns={columns}
              autoHeight={true}
              rows={data}
              disableVirtualization
              disableColumnFilter
              disableColumnMenu
              loading={loading}
              pagination
              paginationMode="server"
              rowCount={totalCount}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize, page: page - 1 },
                },
              }}
              onPaginationModelChange={(model) => {
                // Handle page change
                if (model.page !== page - 1) {
                  setPage(model.page + 1);
                }

                // Handle page size change
                if (model.pageSize !== pageSize) {
                  setPageSize(model.pageSize);
                  setPage(1); // Reset to first page when changing page size
                }
              }}
              checkboxSelection
              onRowSelectionModelChange={handleSelectionModelChange}
              rowSelectionModel={selectedReceivers}
              getRowClassName={() => "data-grid-row"}
              localeText={{
                MuiTablePagination: {
                  labelRowsPerPage:
                    formData.receiverIdsType === "user"
                      ? "مستخدم في الصفحة"
                      : "فني في الصفحة",
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateNotification;
